import { ReactNode, memo, useContext } from "react";
import { Light as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomOneDark } from "react-syntax-highlighter/dist/esm/styles/hljs";
import javascript from "react-syntax-highlighter/dist/esm/languages/hljs/javascript";
import python from "react-syntax-highlighter/dist/esm/languages/hljs/python";
import go from "react-syntax-highlighter/dist/esm/languages/hljs/go";
import php from "react-syntax-highlighter/dist/esm/languages/hljs/php";
import java from "react-syntax-highlighter/dist/esm/languages/hljs/java";
import json from "react-syntax-highlighter/dist/esm/languages/hljs/json";
import plaintext from "react-syntax-highlighter/dist/esm/languages/hljs/plaintext";

import {
  Box,
  Button,
  SxProps,
  Typography,
  alpha,
  styled,
  useTheme,
} from "@mui/material";
import CopyButton from "../copy-button";
import { ChooseCodeLanguageContext } from "../ChooseCodeLanguage/ChooseCodeLanguage";

type Props = {
  code?: string;
  title?: ReactNode;
  /** 是否为json */
  isJson?: boolean;
  /** 是否为text  */
  isText?: boolean;
  /** 最外层容器的sx */
  sx?: SxProps;
};

const codeMap: { [key: string]: any } = {
  javascript: javascript,
  python: python,
  go: go,
  php: php,
  java: java,
  json: json,
  plaintext: plaintext,
};

const CodeSnippet = ({
  title,
  code,
  isJson = false,
  isText = false,
  sx,
}: Props) => {
  const theme = useTheme();
  const { language } = useContext(ChooseCodeLanguageContext);

  SyntaxHighlighter.registerLanguage(language, codeMap[language]);

  return (
    <CodeSnippetWrap sx={sx}>
      <TopHeader title={title || ""} code={code || ""} />
      <SyntaxHighlighter
        language={isJson ? "json" : isText ? "plaintext" : language}
        style={atomOneDark}
        showLineNumbers={true}
        lineNumberStyle={{
          color: theme.palette.NightText.disabled,
          minWidth: "3em",
          // paddingRight: "16px",
          paddingRight: 0,
          textAlign: "left",
          fontSize: "12px",
        }}
      >
        {code || ""}
      </SyntaxHighlighter>
    </CodeSnippetWrap>
  );
};

const CodeSnippetWrap = styled(Box)(({ theme }) => ({
  borderRadius: "16px",
  padding: "16px",
  backgroundColor: `${theme.palette.grey[900]} !important`,
  pre: {
    margin: 0,
    paddingLeft: `0 !important`,
    backgroundColor: `${theme.palette.grey[900]} !important`,
    "::-webkit-scrollbar": {
      // display: "none",
      backgroundColor: "transparent",
      width: "0",
      height: "6px",
    },
    "::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.grey[800],
      borderRadius: "12px",
    },
  },
}));

const TopHeader = ({ title, code }: { title: ReactNode; code: string }) => {
  const theme = useTheme();
  return (
    <TopHeaderWrap>
      <TitleBox>{title}</TitleBox>
      <RightActionBox>
        <CopyButton
          source={code}
          color={theme.palette?.NightText?.secondary}
          sx={
            {
              // mr: 2,
            }
          }
        />
        {/* <TestBtn variant="contained">test</TestBtn> */}
      </RightActionBox>
    </TopHeaderWrap>
  );
};

export const TestBtn = styled(Button)(({ theme }) => ({
  padding: "4px 12px",
  color: theme.palette.primary.light,
  backgroundColor: alpha(theme.palette.primary.main, 0.16),
  ":hover": {
    color: "#fff",
    backgroundColor: alpha(theme.palette.primary.main, 1),
  },
}));

const TopHeaderWrap = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  borderBottom: `1px solid ${theme.palette?.NightText?.stroke}`,
  paddingBottom: "8px",
  marginBottom: "8px",
}));

const TitleBox = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  color: theme.palette?.NightText?.secondary,
  fontWeight: 400,
  lineHeight: "22px",
  fontStyle: "normal",
}));

const RightActionBox = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

export default memo(CodeSnippet);
